import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Icon } from "../icon";
import { formatYoutubeDuration } from "./formatYoutubeDuration";

export type YoutubePlayerControlsProps = {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  onTogglePlay: () => void;
  onSeek: (seconds: number) => void;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
  showFullscreen?: boolean;
  hideControls?: boolean;
  /** When false, bar is hidden (hover-to-show is handled by YoutubePlayerChrome). */
  visible?: boolean;
  onReveal?: () => void;
  onScrubbingChange?: (scrubbing: boolean) => void;
};

const barStyle: CSSProperties = {
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 10,
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "0 12px",
  height: 48,
  background:
    "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.65) 40%, rgba(0,0,0,0.85) 100%)",
  boxSizing: "border-box",
  transition: "opacity 0.2s ease, visibility 0.2s ease",
  pointerEvents: "auto",
};

const iconButtonStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  padding: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  flexShrink: 0,
  touchAction: "manipulation",
  WebkitTapHighlightColor: "transparent",
};

const trackStyle: CSSProperties = {
  flex: 1,
  height: 20,
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  touchAction: "none",
  minWidth: 0,
};

const trackInnerStyle: CSSProperties = {
  position: "relative",
  width: "100%",
  height: 4,
  borderRadius: 2,
  background: "rgba(255,255,255,0.35)",
};

const fillStyle = (pct: number): CSSProperties => ({
  position: "absolute",
  left: 0,
  top: 0,
  bottom: 0,
  width: `${pct}%`,
  borderRadius: 2,
  background: "#ff0000",
  pointerEvents: "none",
});

const thumbStyle = (pct: number): CSSProperties => ({
  position: "absolute",
  left: `${pct}%`,
  top: "50%",
  width: 12,
  height: 12,
  marginLeft: -6,
  marginTop: -6,
  borderRadius: "50%",
  background: "#ff0000",
  pointerEvents: "none",
  transform: "translateY(-50%)",
});

function PauseBars() {
  return (
    <span
      style={{
        display: "inline-flex",
        gap: 5,
        alignItems: "center",
        justifyContent: "center",
      }}
      aria-hidden
    >
      <span
        style={{
          width: 4,
          height: 16,
          background: "#fff",
          borderRadius: 1,
        }}
      />
      <span
        style={{
          width: 4,
          height: 16,
          background: "#fff",
          borderRadius: 1,
        }}
      />
    </span>
  );
}

export function YoutubePlayerControls({
  isPlaying,
  currentTime,
  duration,
  onTogglePlay,
  onSeek,
  onToggleFullscreen,
  isFullscreen = false,
  showFullscreen = true,
  hideControls = false,
  visible = true,
  onReveal,
  onScrubbingChange,
}: YoutubePlayerControlsProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [scrubbing, setScrubbingState] = useState(false);
  const [scrubTime, setScrubTime] = useState(0);

  const safeDuration = duration > 0 ? duration : 0;
  const displayTime = scrubbing ? scrubTime : currentTime;
  const progressPct =
    safeDuration > 0
      ? Math.min(100, Math.max(0, (displayTime / safeDuration) * 100))
      : 0;

  const setScrubbing = useCallback(
    (next: boolean) => {
      setScrubbingState(next);
      onScrubbingChange?.(next);
    },
    [onScrubbingChange],
  );

  const timeFromClientX = useCallback(
    (clientX: number) => {
      const el = trackRef.current;
      if (!el || safeDuration <= 0) return 0;
      const rect = el.getBoundingClientRect();
      const ratio = Math.max(
        0,
        Math.min(1, (clientX - rect.left) / rect.width),
      );
      return ratio * safeDuration;
    },
    [safeDuration],
  );

  const onTrackPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (safeDuration <= 0) return;
    e.preventDefault();
    e.stopPropagation();
    onReveal?.();
    const t = timeFromClientX(e.clientX);
    setScrubTime(t);
    setScrubbing(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onTrackPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!scrubbing || safeDuration <= 0) return;
    setScrubTime(timeFromClientX(e.clientX));
  };

  const onTrackPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!scrubbing) return;
    const t = timeFromClientX(e.clientX);
    setScrubbing(false);
    onSeek(t);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    onReveal?.();
  };

  useEffect(() => {
    if (!visible) setScrubbing(false);
  }, [visible, setScrubbing]);

  if (hideControls) return null;

  return (
    <div
      role="group"
      aria-label="Video controls"
      style={{
        ...barStyle,
        opacity: visible ? 1 : 0,
        visibility: visible ? "visible" : "hidden",
        pointerEvents: visible ? "auto" : "none",
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        style={iconButtonStyle}
        onClick={(e) => {
          e.stopPropagation();
          onTogglePlay();
          onReveal?.();
        }}
        aria-label={isPlaying ? "Pause" : "Play"}
      >
        {isPlaying ? (
          <PauseBars />
        ) : (
          <Icon name="playIcon" width={18} height={18} color="#fff" />
        )}
      </button>

      <div
        ref={trackRef}
        style={trackStyle}
        onPointerDown={onTrackPointerDown}
        onPointerMove={onTrackPointerMove}
        onPointerUp={onTrackPointerUp}
        onPointerCancel={onTrackPointerUp}
        role="slider"
        aria-label="Seek"
        aria-valuemin={0}
        aria-valuemax={safeDuration}
        aria-valuenow={displayTime}
        tabIndex={visible ? 0 : -1}
        onKeyDown={(e) => {
          if (safeDuration <= 0) return;
          const step = e.shiftKey ? 10 : 5;
          if (e.key === "ArrowRight") {
            e.preventDefault();
            onSeek(Math.min(safeDuration, displayTime + step));
          } else if (e.key === "ArrowLeft") {
            e.preventDefault();
            onSeek(Math.max(0, displayTime - step));
          }
        }}
      >
        <div style={trackInnerStyle}>
          <div style={fillStyle(progressPct)} />
          <div style={thumbStyle(progressPct)} />
        </div>
      </div>

      <span
        style={{
          color: "#fff",
          fontSize: 12,
          fontVariantNumeric: "tabular-nums",
          flexShrink: 0,
          minWidth: 72,
          textAlign: "right",
        }}
      >
        {formatYoutubeDuration(displayTime)}
        {" / "}
        {formatYoutubeDuration(safeDuration)}
      </span>

      {showFullscreen && onToggleFullscreen ? (
        <button
          type="button"
          style={iconButtonStyle}
          onClick={(e) => {
            e.stopPropagation();
            onToggleFullscreen();
            onReveal?.();
          }}
          aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
        >
          <Icon
            name={isFullscreen ? "fullScreenExitIcon" : "fullScreenIcon"}
            size={20}
            color="#fff"
          />
        </button>
      ) : null}
    </div>
  );
}
